import { createTenantClient } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Tenant scope matters here — SCM PAT storage and repo scans are all
// tenant-partitioned, so route through the shared factory so every request
// carries the caller's active ``X-Tenant-ID`` and auth token.
const api = createTenantClient(API_BASE_URL);

// ========== Scan Operations ==========

export const uploadDependencyFile = async (file, name = null, frameworkId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (name) formData.append('name', name);
  if (frameworkId) formData.append('linked_framework_id', frameworkId);
  
  const response = await api.post('/api/litesabre/scan/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const scanRepository = async (repositoryUrl, branch = 'main', scanTypes = ['dependency'], frameworkId = null) => {
  const response = await api.post('/api/litesabre/scan/repository', {
    repository_url: repositoryUrl,
    branch,
    scan_types: scanTypes,
    linked_framework_id: frameworkId
  });
  return response.data;
};

export const scanContainer = async (imageName, registry = null, frameworkId = null) => {
  const response = await api.post('/api/litesabre/scan/container', {
    image_name: imageName,
    registry,
    linked_framework_id: frameworkId
  });
  return response.data;
};

export const scanIaC = async (content, configType, fileName = null, frameworkId = null) => {
  const response = await api.post('/api/litesabre/scan/iac', {
    content,
    config_type: configType,
    file_name: fileName,
    linked_framework_id: frameworkId
  });
  return response.data;
};

// ========== Scan History ==========

export const getScans = async (params = {}) => {
  const response = await api.get('/api/litesabre/scans', { params });
  return response.data;
};

export const getScan = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}`);
  return response.data;
};

export const deleteScan = async (scanId) => {
  const response = await api.delete(`/api/litesabre/scans/${scanId}`);
  return response.data;
};

// ========== Scan Progress & History ==========

export const getScanProgress = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/progress`);
  return response.data;
};

export const getScanFindings = async (scanId, params = {}) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/findings`, { params });
  return response.data;
};

export const getScanHistory = async (limit = 20, scanType = null, status = null) => {
  const params = { limit };
  if (scanType) params.scan_type = scanType;
  if (status) params.status = status;
  const response = await api.get('/api/litesabre/scan-history', { params });
  return response.data;
};

// ========== Scan Diff & Trend Analysis ==========

export const getScanTrends = async (target, limit = 20) => {
  const response = await api.get('/api/litesabre/scan-trends', { params: { target, limit } });
  return response.data;
};

export const getScanTargets = async () => {
  const response = await api.get('/api/litesabre/scan-targets');
  return response.data;
};

export const getScanDiff = async (scanIdA, scanIdB) => {
  const response = await api.get(`/api/litesabre/scan-diff/${scanIdA}/${scanIdB}`);
  return response.data;
};

// ========== Vulnerabilities ==========

export const getVulnerabilities = async (params = {}) => {
  const response = await api.get('/api/litesabre/vulnerabilities', { params });
  return response.data;
};

export const getVulnerability = async (vulnId) => {
  const response = await api.get(`/api/litesabre/vulnerabilities/${vulnId}`);
  return response.data;
};

// ========== CVE Lookup ==========

export const lookupCVE = async (cveId) => {
  const response = await api.get(`/api/litesabre/cve/${cveId}`);
  return response.data;
};

export const searchCVEs = async (keyword, startIndex = 0, resultsPerPage = 20) => {
  const response = await api.get('/api/litesabre/cve/search', {
    params: { keyword, start_index: startIndex, results_per_page: resultsPerPage }
  });
  return response.data;
};

// ========== Control Linkage ==========

export const linkVulnerabilityToControl = async (vulnId, controlId, frameworkId, notes = null) => {
  const response = await api.post(`/api/litesabre/vulnerabilities/${vulnId}/link-control`, {
    control_id: controlId,
    framework_id: frameworkId,
    notes
  });
  return response.data;
};

export const getControlLinks = async (params = {}) => {
  const response = await api.get('/api/litesabre/control-links', { params });
  return response.data;
};

export const updateLinkStatus = async (linkId, status, notes = null) => {
  const params = { status };
  if (notes) params.notes = notes;
  const response = await api.put(`/api/litesabre/control-links/${linkId}/status`, null, { params });
  return response.data;
};

// ========== Dashboard & Stats ==========

export const getLiteSabreDashboard = async () => {
  const response = await api.get('/api/litesabre/dashboard');
  return response.data;
};

export const getScanStats = async () => {
  const response = await api.get('/api/litesabre/stats');
  return response.data;
};

// ========== Export Functions ==========

export const exportScanJSON = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/export/json`, {
    responseType: 'blob'
  });
  
  // Create download link
  const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `litesaber-scan-${scanId}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return response.data;
};

export const exportScanPDF = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/export/pdf`, {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `litesaber-scan-${scanId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return true;
};

export const exportScanMarkdown = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/export/markdown`, {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `litesaber-scan-${scanId}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return true;
};

export const exportScanHTML = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/export/html`, {
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `litesaber-scan-${scanId}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return true;
};

// ========== Helper Functions ==========

export const getSeverityColor = (severity) => {
  const colors = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-white',
    low: 'bg-blue-500 text-white',
    unknown: 'bg-gray-500 text-white',
    none: 'bg-green-500 text-white'
  };
  return colors[severity?.toLowerCase()] || colors.unknown;
};

export const getSeverityBadgeColor = (severity) => {
  const colors = {
    critical: 'border-red-600 text-red-600 bg-red-50',
    high: 'border-orange-500 text-orange-600 bg-orange-50',
    medium: 'border-yellow-500 text-yellow-600 bg-yellow-50',
    low: 'border-blue-500 text-blue-600 bg-blue-50',
    unknown: 'border-gray-500 text-gray-600 bg-gray-50',
    none: 'border-green-500 text-green-600 bg-green-50'
  };
  return colors[severity?.toLowerCase()] || colors.unknown;
};

export const formatScanType = (scanType) => {
  const types = {
    dependency: 'Dependency Scan',
    container: 'Container Scan',
    iac: 'IaC Scan',
    code: 'Code Scan'
  };
  return types[scanType] || scanType;
};

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

// Rescan
export const retriageScan = async (scanId) => {
  const response = await api.post(`/api/litesabre/scans/${scanId}/retriage`);
  return response.data;
};

export const setFindingFalsePositive = async (scanId, findingId, falsePositive, reason) => {
  const response = await api.post(
    `/api/litesabre/scans/${scanId}/findings/${encodeURIComponent(findingId)}/false-positive`,
    { false_positive: falsePositive, reason },
  );
  return response.data;
};

export const rescanTarget = async (scanId) => {
  const response = await api.post(`/api/litesabre/scans/${scanId}/rescan`);
  return response.data;
};

// Get dependencies with health and license info
export const getScanDependencies = async (scanId, includeHealth = true) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/dependencies`, {
    params: { include_health: includeHealth }
  });
  return response.data;
};

// Generate Fix PR content
export const generateFixPR = async (scanId, targetFile = 'requirements.txt') => {
  const response = await api.post(`/api/litesabre/scans/${scanId}/generate-fix-pr`, {
    target_file: targetFile
  });
  return response.data;
};

// Create an actual fix PR on GitHub
export const createFixPR = async (scanId, options = {}) => {
  const response = await api.post(`/api/litesabre/scans/${scanId}/create-fix-pr`, {
    repository_url: options.repositoryUrl,
    target_file: options.targetFile || 'requirements.txt',
    base_branch: options.baseBranch || 'main'
  });
  return response.data;
};

// Get security overview
export const getSecurityOverview = async (scanId) => {
  const response = await api.get(`/api/litesabre/scans/${scanId}/security-overview`);
  return response.data;
};

export default {
  // Scans
  uploadDependencyFile,
  scanRepository,
  scanContainer,
  scanIaC,
  getScans,
  getScan,
  deleteScan,
  rescanTarget,
  
  // Vulnerabilities
  getVulnerabilities,
  getVulnerability,
  
  // CVE
  lookupCVE,
  searchCVEs,
  
  // Control Links
  linkVulnerabilityToControl,
  getControlLinks,
  updateLinkStatus,
  
  // Dashboard
  getLiteSabreDashboard,
  getScanStats,
  
  // Dependencies & Security
  getScanDependencies,
  generateFixPR,
  createFixPR,
  getSecurityOverview,
  
  // Export
  exportScanJSON,
  exportScanPDF,
  exportScanMarkdown,
  exportScanHTML,
  
  // Helpers
  getSeverityColor,
  getSeverityBadgeColor,
  formatScanType,
  formatDate
};
